import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { ExternalLink, RotateCw, Trash2, X } from 'lucide-react';
import { useTranslation } from '../../../lib/i18n';
import { useProductName } from '../../../lib/use-brand';
import {
  HELLO_MESSAGE,
  REVIEW_HELLO_INTERVAL_MS,
  REVIEW_HELLO_TIMEOUT_MS,
  ELEMENT_KIND_KEYS,
  acceptReviewEvent,
  elementKind,
  highlightMessage,
  modeMessage,
  normalizePageUrl,
  ownerElementLabel,
  pageLabel,
  parseReviewMessage,
  pinsForPage,
  revealDelta,
  reviewHostAllowed,
  reviewHref,
  reviewMessage,
  reviewUrl,
  urlOrigin,
  type ReviewPick,
} from '../../../lib/site-review';
import { addReviewComment, beginReviewSend, clearReview, deleteReviewComment, onChatTurnFinished, useReviewComments, useReviewSending } from '../../../lib/site-edit-store';
import { Modal } from '../../Modal';
import { Badge, Button, Card, IconButton, ScrollArea, SegmentedToggle, Typography } from '../ui';
import styles from './SiteReviewFrame.module.css';

// The in-app review of a Symphony-built site (contract §2), as the web host: the LIVE site in an iframe, the review
// bridge's handshake (`symphony-review:hello` until `ready`), the Browse / Comment toggle, a comment editor on every
// pick, the pins, the comments list, and Finish / Discard. One component for both web presentations: `pane` (beside
// the chat on a computer) and `sheet` (full screen, on a phone or a narrow window).
//
// Every rule is `lib/site-review.ts` (unit-tested, mirrored by both phones' review screens). A web review sends NO
// crop — the parent cannot read a cross-origin iframe's pixels (contract §2.4): a written-down difference from the
// phones, which attach one image per comment.

type Handshake = 'loading' | 'waiting' | 'ready' | 'unavailable';

export function SiteReviewFrame({
  previewUrl,
  reviewKey,
  layout,
  conversationId,
  onFinish,
  onClose,
  onAskRepublish,
}: {
  previewUrl: string;
  /** Where the comments are kept (`reviewKey`): per site and conversation. */
  reviewKey: string;
  layout: 'pane' | 'sheet';
  /** The chat this review is beside; a turn ending there reloads the site (the agent may have published). */
  conversationId?: string | null;
  /**
   * Finish: the contract message, built. The caller SENDS it to the site's chat (`finishTarget`) and returns that chat
   * (null = a new one). The comments stay until the message lands there (`observeReviewSend`), so a failed send loses
   * nothing; Finish is disabled meanwhile.
   */
  onFinish: (message: string) => string | null;
  /** Close, after the discard question when there are unsent comments. */
  onClose: () => void;
  onAskRepublish: () => void;
}) {
  const { t } = useTranslation();
  const productName = useProductName();
  const comments = useReviewComments(reviewKey);
  const sending = useReviewSending(reviewKey);
  const frameRef = useRef<HTMLIFrameElement | null>(null);
  const wrapRef = useRef<HTMLDivElement | null>(null);
  const origin = urlOrigin(previewUrl);
  const hostAllowed = typeof window !== 'undefined' && reviewHostAllowed(window.location.origin);

  const [page, setPage] = useState(() => reviewHref(previewUrl));
  const [src, setSrc] = useState(() => reviewUrl(previewUrl));
  const [reloads, setReloads] = useState(0);
  const [handshake, setHandshake] = useState<Handshake>('loading');
  const [picking, setPicking] = useState(true);
  const [pick, setPick] = useState<ReviewPick | null>(null);
  const [draft, setDraft] = useState('');
  const [confirm, setConfirm] = useState<'close' | 'discard' | null>(null);
  const [listOpen, setListOpen] = useState(layout === 'pane');

  const post = useCallback(
    (message: object) => {
      const target = frameRef.current?.contentWindow;
      if (target && origin) target.postMessage(message, origin);
    },
    [origin],
  );

  // What the page shows, re-sent after every `ready` and every add/delete.
  const commentsRef = useRef(comments);
  commentsRef.current = comments;
  const pageRef = useRef(page);
  pageRef.current = page;
  const pickingRef = useRef(picking);
  pickingRef.current = picking;
  const sync = useCallback(() => {
    post(modeMessage(pickingRef.current));
    post(highlightMessage(pinsForPage(commentsRef.current, pageRef.current)));
  }, [post]);

  // The handshake: hello on every load, then every 500 ms until `ready`; no ready in 10 s = review unavailable.
  const helloTimer = useRef<number | null>(null);
  const stopHello = () => {
    if (helloTimer.current !== null) window.clearInterval(helloTimer.current);
    helloTimer.current = null;
  };
  const onFrameLoad = () => {
    stopHello();
    if (!hostAllowed) {
      setHandshake('unavailable');
      return;
    }
    setHandshake('waiting');
    const started = Date.now();
    post(HELLO_MESSAGE);
    helloTimer.current = window.setInterval(() => {
      if (Date.now() - started >= REVIEW_HELLO_TIMEOUT_MS) {
        stopHello();
        setHandshake((h) => (h === 'ready' ? h : 'unavailable'));
        return;
      }
      post(HELLO_MESSAGE);
    }, REVIEW_HELLO_INTERVAL_MS);
  };
  useEffect(() => stopHello, []);

  useEffect(() => {
    const onMessage = (event: MessageEvent) => {
      if (!acceptReviewEvent({ origin: event.origin, fromFrame: event.source === frameRef.current?.contentWindow }, previewUrl)) return;
      const message = parseReviewMessage(event.data);
      if (!message) return;
      if (message.type === 'symphony-review:ready') {
        stopHello();
        setHandshake('ready');
        pageRef.current = message.pageUrl;
        setPage(message.pageUrl);
        sync();
        return;
      }
      // A single-page site changes route without a new `ready`: the pick's own page is the one it is on.
      setPick(message);
      setDraft('');
    };
    window.addEventListener('message', onMessage);
    return () => window.removeEventListener('message', onMessage);
  }, [previewUrl, sync]);

  useEffect(() => {
    if (handshake === 'ready') post(modeMessage(picking));
  }, [picking, handshake, post]);
  useEffect(() => {
    if (handshake === 'ready') post(highlightMessage(pinsForPage(comments, page)));
  }, [comments, page, handshake, post]);

  // Reload keeps the page the owner is on, in review mode.
  const reload = useCallback(() => {
    setSrc(reviewUrl(pageRef.current || previewUrl));
    setReloads((n) => n + 1);
    setHandshake('loading');
  }, [previewUrl]);
  useEffect(() => {
    if (!conversationId) return undefined;
    return onChatTurnFinished((id) => {
      if (id === conversationId) reload();
    });
  }, [conversationId, reload]);

  const saveComment = () => {
    const text = draft.trim();
    if (!pick || !text) return;
    addReviewComment(reviewKey, { pageUrl: pick.pageUrl, selector: pick.selector, text: pick.text, tag: pick.tag, comment: text });
    setPick(null);
    setDraft('');
    setListOpen(true);
  };

  const focusComment = (index: number) => {
    const c = comments[index];
    if (!c || normalizePageUrl(c.pageUrl) !== normalizePageUrl(page)) return;
    const pins = pinsForPage(comments, page);
    const label = String(index + 1);
    const ordered = [...pins.filter((p) => p.label === label), ...pins.filter((p) => p.label !== label)];
    post(highlightMessage(ordered, true));
  };

  const finish = () => {
    if (comments.length === 0 || sending) return;
    const message = reviewMessage(t('siteReview.finishMessage.lead'), comments);
    const target = onFinish(message);
    beginReviewSend(reviewKey, message, target);
  };

  const requestClose = () => (comments.length > 0 ? setConfirm('close') : onClose());
  const confirmed = () => {
    const what = confirm;
    setConfirm(null);
    clearReview(reviewKey);
    setPick(null);
    if (what === 'close') onClose();
  };

  // Escape closes the editor first, then the review (the sheet only — the pane leaves Escape to the chat).
  useEffect(() => {
    if (layout !== 'sheet') return undefined;
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Escape' || confirm) return;
      if (pick) setPick(null);
      else requestClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  });

  const editorStyle = useMemo(() => {
    if (layout !== 'pane' || !pick?.rect) return undefined;
    const box = wrapRef.current?.getBoundingClientRect();
    const width = Math.min(320, (box?.width ?? 360) - 16);
    const height = box?.height ?? 600;
    const left = Math.max(8, Math.min(pick.rect.x, (box?.width ?? 360) - width - 8));
    const below = pick.rect.y + pick.rect.h + 8;
    const top = below + 200 < height ? below : Math.max(8, pick.rect.y - 208);
    return { left, top, width };
  }, [layout, pick]);

  // A docked editor (a phone's bottom sheet, or a pane too short to float it) covers the bottom of the preview — in the
  // production report, exactly the element just picked. The page is cross-origin, so it cannot be scrolled from here:
  // the iframe itself moves up by `revealDelta` until the pick sits above the sheet, and again whenever what is visible
  // changes (the keyboard opening resizes the visual viewport, the textarea grows). Back to 0 when the editor closes.
  const editorRef = useRef<HTMLDivElement | null>(null);
  const [shift, setShift] = useState(0);
  const docked = pick !== null && (layout === 'sheet' || !editorStyle);
  useLayoutEffect(() => {
    const rect = pick?.rect;
    if (!docked || !rect) {
      setShift(0);
      return undefined;
    }
    const measure = () => {
      const wrap = wrapRef.current?.getBoundingClientRect();
      const editor = editorRef.current?.getBoundingClientRect();
      if (!wrap || !editor) return;
      const vv = window.visualViewport;
      const vvTop = vv?.offsetTop ?? 0;
      const vvBottom = vvTop + (vv?.height ?? window.innerHeight);
      const visible = { top: Math.max(0, vvTop - wrap.top), bottom: Math.min(editor.top, vvBottom) - wrap.top };
      setShift(Math.max(0, Math.min(revealDelta(rect, visible), Math.max(0, wrap.height - visible.top))));
    };
    measure();
    const vv = window.visualViewport;
    vv?.addEventListener('resize', measure);
    vv?.addEventListener('scroll', measure);
    window.addEventListener('resize', measure);
    const observer = typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(measure);
    if (editorRef.current) observer?.observe(editorRef.current);
    return () => {
      vv?.removeEventListener('resize', measure);
      vv?.removeEventListener('scroll', measure);
      window.removeEventListener('resize', measure);
      observer?.disconnect();
    };
  }, [docked, pick]);

  const reviewing = handshake === 'ready';
  // The owner reads "Heading", not "h1"; the finished message keeps the raw tag for the agent (contract §2.5).
  const elementLabel = (tag: string, text: string) => ownerElementLabel(t(ELEMENT_KIND_KEYS[elementKind(tag)]), text);
  const element = pick ? elementLabel(pick.tag, pick.text) : '';

  return (
    <div className={`${styles.root} ${layout === 'sheet' ? styles.sheet : styles.pane}`} data-testid={`siteReview-${layout}`}>
      <header className={styles.toolbar}>
        <Typography variant="p3" color="secondary" noWrap className={styles.address} title={page}>
          {pageLabel(page)}
        </Typography>
        <div className={styles.tools}>
          <IconButton aria-label={t('siteReview.reload')} title={t('siteReview.reload')} variant="text" size="small" onClick={reload}>
            <RotateCw size={16} aria-hidden />
          </IconButton>
          <IconButton
            aria-label={t('siteReview.openExternal')}
            title={t('siteReview.openExternal')}
            variant="text"
            size="small"
            onClick={() => window.open(normalizePageUrl(page) || previewUrl, '_blank', 'noopener,noreferrer')}
          >
            <ExternalLink size={16} aria-hidden />
          </IconButton>
          <IconButton aria-label={t('siteReview.close')} title={t('siteReview.close')} variant="text" size="small" onClick={requestClose} data-testid="siteReviewClose">
            <X size={18} aria-hidden />
          </IconButton>
        </div>
      </header>
      {reviewing && (
        <div className={styles.modeRow}>
          <SegmentedToggle
            ariaLabel={t('siteReview.title')}
            size="small"
            segmentWidth={layout === 'sheet' ? 'fill' : 'hug'}
            value={picking ? 'comment' : 'browse'}
            onChange={(v) => setPicking(v === 'comment')}
            options={[
              { value: 'browse', label: t('siteReview.mode.browse') },
              { value: 'comment', label: t('siteReview.mode.comment') },
            ]}
          />
          <Typography variant="p3" color="secondary" className={styles.hint}>
            {t(picking ? 'siteReview.hint.comment' : 'siteReview.hint.browse')}
          </Typography>
        </div>
      )}
      {handshake === 'unavailable' && (
        <div className={styles.notice} role="status" data-testid="siteReviewUnavailable">
          <Typography variant="p3" color="primary">
            {t('siteReview.unavailable')}
          </Typography>
          {hostAllowed && (
            <Button size="small" variant="outlined" onClick={onAskRepublish}>
              {t('siteReview.askRepublish', { productName })}
            </Button>
          )}
        </div>
      )}
      <div className={styles.frameWrap} ref={wrapRef}>
        {src && (
          <iframe
            key={reloads}
            ref={frameRef}
            src={src}
            title={t('siteReview.title')}
            className={styles.frame}
            style={shift > 0 ? { transform: `translateY(${-shift}px)` } : undefined}
            onLoad={onFrameLoad}
            referrerPolicy="strict-origin-when-cross-origin"
          />
        )}
        {handshake === 'loading' && (
          <div className={styles.loading}>
            <Typography variant="p3" color="secondary">
              {t('siteReview.loading')}
            </Typography>
          </div>
        )}
        {pick && (
          <div
            ref={editorRef}
            className={`${styles.editor} ${docked ? styles.editorDocked : ''}`}
            style={editorStyle}
            role="dialog"
            aria-label={t('siteReview.mode.comment')}
            data-testid="siteReviewEditor"
          >
            <Typography variant="p3" color="secondary" noWrap>
              {element}
            </Typography>
            <textarea
              className={styles.textarea}
              value={draft}
              autoFocus
              rows={3}
              maxLength={2000}
              placeholder={t('siteReview.comment.placeholder')}
              onChange={(e) => setDraft(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) saveComment();
              }}
              data-testid="siteReviewCommentInput"
            />
            <div className={styles.editorActions}>
              <Button size="small" variant="text" onClick={() => setPick(null)}>
                {t('siteReview.comment.cancel')}
              </Button>
              <Button size="small" color="accent" disabled={!draft.trim()} onClick={saveComment} data-testid="siteReviewCommentAdd">
                {t('siteReview.comment.add')}
              </Button>
            </div>
          </div>
        )}
      </div>
      {reviewing && (
        <footer className={styles.footer}>
          <Button
            variant="text"
            size="small"
            className={styles.listToggle}
            aria-expanded={listOpen}
            onClick={() => setListOpen((v) => !v)}
            endIcon={comments.length > 0 ? <Badge variant="waiting-on-you" label={String(comments.length)} /> : undefined}
            data-testid="siteReviewCommentsToggle"
          >
            {t('siteReview.comments.title')}
          </Button>
          {listOpen && (
            <ScrollArea component="ol" className={styles.list} data-testid="siteReviewComments">
              {comments.length === 0 && (
                <li className={styles.empty}>
                  <Typography variant="p3" color="secondary">
                    {t('siteReview.comments.empty')}
                  </Typography>
                </li>
              )}
              {comments.map((c, i) => {
                const here = normalizePageUrl(c.pageUrl) === normalizePageUrl(page);
                return (
                  <li key={`${i}-${c.selector}`} className={styles.item}>
                    <Card
                      variant="outline"
                      size="sm"
                      component="button"
                      interactive={here}
                      className={styles.itemBody}
                      onClick={() => focusComment(i)}
                    >
                      <Badge variant="waiting-on-you" label={String(i + 1)} className={styles.pin} />
                      <span className={styles.itemText}>
                        <Typography variant="p3" color="primary" className={styles.clamp2}>
                          {c.comment}
                        </Typography>
                        <Typography variant="p3" color="secondary" noWrap>
                          {here ? elementLabel(c.tag, c.text) : pageLabel(c.pageUrl)}
                        </Typography>
                      </span>
                    </Card>
                    <IconButton aria-label={t('siteReview.comment.delete')} variant="text" size="tiny" onClick={() => deleteReviewComment(reviewKey, i)}>
                      <Trash2 size={14} aria-hidden />
                    </IconButton>
                  </li>
                );
              })}
            </ScrollArea>
          )}
          <div className={styles.footerActions}>
            <Button size="small" variant="text" disabled={comments.length === 0} onClick={() => setConfirm('discard')}>
              {t('siteReview.discard')}
            </Button>
            <Button size="small" color="accent" disabled={comments.length === 0 || sending} onClick={finish} data-testid="siteReviewFinish">
              {t('siteReview.finish')}
            </Button>
          </div>
        </footer>
      )}
      <Modal
        open={confirm !== null}
        onClose={() => setConfirm(null)}
        size="small"
        maxHeightPx={650}
        scrollBody
        fullScreenOnSmall={false}
        title={t('siteReview.discardConfirm.title')}
        footer={
          <>
            <Button variant="outlined" onClick={() => setConfirm(null)}>
              {t('siteReview.discardConfirm.keep')}
            </Button>
            <Button color="destructive" onClick={confirmed} data-testid="siteReviewDiscardConfirm">
              {t('siteReview.discard')}
            </Button>
          </>
        }
      >
        <Typography variant="p2" color="primary">
          {t('siteReview.discardConfirm.body')}
        </Typography>
      </Modal>
    </div>
  );
}
